import styles from './Timeline.module.scss';

import {useSignal, useSignalEffect} from '@preact/signals-react';
import clsx from 'clsx';
import {useLayoutEffect, useMemo, useRef} from 'react';
import {
  TIMELINE_SHORTCUTS,
  TimelineContextProvider,
  TimelineState,
  useApplication,
  useShortcuts,
  useSurfaceShortcuts,
} from '../../contexts';
import {
  useDuration,
  usePlayerTime,
  usePreviewSettings,
  useSharedSettings,
  useSize,
  useStateChange,
  useStorage,
  useSubscribableValue,
} from '../../hooks';
import {labelClipDraggingLeftSignal} from '../../signals';
import {MouseButton, MouseMask, clamp} from '../../utils';
import {AddTrackHeader, AudioTrackRow, AudioTrackRowHeader} from './AudioTrack';
import {Playhead} from './Playhead';
import {SceneTrack} from './SceneTrack';
import {Timestamps} from './Timestamps';

const ZOOM_SPEED = 0.1;
const ZOOM_MIN = 0.5;
const TIMESTAMP_SPACING = 32;
const VIRTUAL_SCROLL_SPACING = 256;
const MAX_FRAME_SIZE = 128;

export function Timeline() {
  const shortcutRef = useSurfaceShortcuts<HTMLDivElement>(TIMELINE_SHORTCUTS);
  const {player, meta} = useApplication();
  const {range} = useSharedSettings();
  const tracks = useSubscribableValue(player.onAudioTracksChanged as any) as import('@cascade/core').AudioTrackConfig[];
  const allClips = useSubscribableValue(player.onAudioClipsChanged as any) as import('@cascade/core').AudioClipConfig[];
  const containerRef = useRef<HTMLDivElement>();
  const playheadRef = useRef<HTMLDivElement>();
  const duration = useDuration();
  const {fps} = usePreviewSettings();
  const rect = useSize(containerRef);
  const [offset, setOffset] = useStorage('timeline-offset', 0);
  const [scale, setScale] = useStorage('timeline-scale', 1);
  const seeking = useSignal<number | null>(null);
  const isReady = duration > 0;

  const {durationTime} = usePlayerTime();

  useLayoutEffect(() => {
    containerRef.current.scrollLeft = offset;
  }, [rect.width > 0 && isReady]);

  const sizes = useMemo(
    () => ({
      viewLength: rect.width,
      paddingLeft: 0,
      fullLength: rect.width * scale + rect.width / 2,
      playableLength: rect.width * scale,
    }),
    [rect.width, scale],
  );

  const zoomMax = (MAX_FRAME_SIZE / sizes.viewLength) * duration;

  const safeDuration = duration || 1;
  const safeDurationTime = durationTime || 1;

  const conversion = useMemo(
    () => ({
      framesToPixels: (value: number) =>
        (value / safeDuration) * sizes.playableLength,
      secondsToPixels: (value: number) =>
        (value / safeDurationTime) * sizes.playableLength,
      framesToPercents: (value: number) => (value / safeDuration) * 100,
      secondsToPercents: (value: number) => (value / safeDurationTime) * 100,
      pixelsToFrames: (value: number) =>
        (value / sizes.playableLength) * safeDuration,
      pixelsToSeconds: (value: number) =>
        (value / sizes.playableLength) * safeDurationTime,
    }),
    [safeDuration, safeDurationTime, sizes],
  );

  const state = useMemo<TimelineState>(() => {
    const density = Math.pow(
      2,
      Math.round(Math.log2(safeDuration / sizes.playableLength)),
    );
    const segmentDensity = Math.floor(TIMESTAMP_SPACING * density);
    const virtualScrollDensity = Math.max(
      1,
      Math.floor(VIRTUAL_SCROLL_SPACING * density),
    );
    const relativeOffset = offset - sizes.paddingLeft;
    const firstVisibleFrame = Math.max(
      0,
      Math.floor(
        conversion.pixelsToFrames(relativeOffset) / virtualScrollDensity,
      ) * virtualScrollDensity,
    );
    const firstVisibleTime = player.status.framesToSeconds(firstVisibleFrame);
    const lastVisibleFrame =
      Math.ceil(
        conversion.pixelsToFrames(
          relativeOffset + sizes.viewLength + TIMESTAMP_SPACING,
        ) / virtualScrollDensity,
      ) * virtualScrollDensity;
    const lastVisibleTime = player.status.framesToSeconds(lastVisibleFrame);
    const startPosition = sizes.paddingLeft + rect.x - offset;

    return {
      viewLength: sizes.viewLength,
      offset: relativeOffset,
      firstVisibleTime,
      firstVisibleFrame,
      lastVisibleTime,
      lastVisibleFrame,
      density,
      segmentDensity,
      pointerToSeconds: (value: number) =>
        conversion.pixelsToSeconds(value - startPosition),
      pointerToFrames: (value: number) =>
        conversion.pixelsToFrames(value - startPosition),
      ...conversion,
    };
  }, [sizes, conversion, duration, offset]);

  useStateChange(
    ([prevDuration, prevWidth]) => {
      const newDuration = duration / fps;
      let newScale = scale;
      if (prevDuration !== 0 && newDuration !== 0) {
        newScale *= newDuration / prevDuration;
      }
      if (prevWidth !== 0 && rect.width !== 0) {
        newScale *= prevWidth / rect.width;
      }
      if (!isNaN(newScale) && duration > 0) {
        setScale(clamp(ZOOM_MIN, zoomMax, newScale));
      }
    },
    [duration / fps, rect.width],
  );

  useShortcuts(TIMELINE_SHORTCUTS, {
    focusPlayhead: () => {
      const maxOffset = sizes.fullLength - sizes.viewLength;
      const scrollLeft = state.secondsToPixels(player.status.time);
      const newOffset = clamp(0, maxOffset, scrollLeft);
      containerRef.current.scrollLeft = newOffset;
      setOffset(newOffset);
    },
    moveRangeStart: () => {
      const frame = player.onFrameChanged.current;
      const end = player.status.secondsToFrames(range[1]);
      meta.shared.range.update(frame, end, duration, fps);
    },
    moveRangeEnd: () => {
      const frame = player.onFrameChanged.current;
      const start = player.status.secondsToFrames(range[0]);
      meta.shared.range.update(start, frame, duration, fps);
    },
  });

  useLayoutEffect(() => {
    containerRef.current.scrollLeft = offset;
  }, [scale]);

  useSignalEffect(() => {
    const offset = labelClipDraggingLeftSignal.value;
    if (offset !== null && playheadRef.current) {
      playheadRef.current.style.left = `${
        state.secondsToPixels(offset) + sizes.paddingLeft
      }px`;
    }
  });

  const scrub = (x: number) => {
    const frame = Math.floor(state.pointerToFrames(x));

    seeking.value = player.clampRange(frame);
    if (player.onFrameChanged.current !== frame) {
      player.requestSeek(frame);
    }
  };

  return (
    <TimelineContextProvider state={state}>
      <div
        ref={shortcutRef}
        className={clsx(styles.root, styles.show)}
      >
        <div className={styles.timelineBody}>
          <div className={styles.trackHeaders}>
            <div className={styles.sceneTrackHeader}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 6.47L5.76 10H20v8H4V6.47M22 4h-4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4z" />
              </svg>
            </div>
            {(tracks ?? []).map(track => (
              <AudioTrackRowHeader key={track.id} track={track} />
            ))}
            <AddTrackHeader />
          </div>
          <div
            className={styles.timelineWrapper}
            ref={containerRef}
            onScroll={event =>
              setOffset((event.target as HTMLElement).scrollLeft)
            }
            onWheel={event => {
              const isVertical =
                Math.abs(event.deltaX) > Math.abs(event.deltaY);
              if (event.shiftKey || isVertical) return;
              event.preventDefault();

              let ratio = 1 - Math.sign(event.deltaY) * ZOOM_SPEED;
              let newScale = scale * ratio;
              if (newScale < ZOOM_MIN) {
                newScale = ZOOM_MIN;
                ratio = newScale / scale;
              }
              if (newScale > zoomMax) {
                newScale = zoomMax;
                ratio = newScale / scale;
              }
              if (newScale === scale) {
                return;
              }

              const pointer = offset - sizes.paddingLeft + event.clientX - rect.x;
              const newTrackSize = rect.width * newScale * +rect.width;
              const maxOffset = newTrackSize - rect.width;
              const newOffset = clamp(
                0,
                maxOffset,
                offset - pointer + pointer * ratio,
              );

              containerRef.current.scrollLeft = newOffset;
              if (!isNaN(newScale)) {
                setScale(newScale);
              }
              if (!isNaN(newOffset)) {
                setOffset(newOffset);
              }
              playheadRef.current.style.left = `${
                event.clientX - rect.x + newOffset
              }px`;
            }}
            onPointerDown={event => {
              if (event.button === MouseButton.Left) {
                event.preventDefault();
                event.currentTarget.setPointerCapture(event.pointerId);
                playheadRef.current.style.display = 'none';
                scrub(event.clientX);
              } else if (event.button === MouseButton.Middle) {
                event.preventDefault();
                event.currentTarget.setPointerCapture(event.pointerId);
                containerRef.current.style.cursor = 'grabbing';
              }
            }}
            onPointerMove={event => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                if (event.buttons & MouseMask.Primary) {
                  scrub(event.clientX);
                } else if (event.buttons & MouseMask.Auxiliary) {
                  const newOffset = clamp(
                    0,
                    sizes.playableLength,
                    offset - event.movementX,
                  );
                  setOffset(newOffset);
                  containerRef.current.scrollLeft = newOffset;
                }
              } else if (labelClipDraggingLeftSignal.value === null) {
                playheadRef.current.style.left = `${
                  event.clientX - rect.x + offset
                }px`;
              }
            }}
            onPointerUp={event => {
              if (labelClipDraggingLeftSignal.value === null) {
                playheadRef.current.style.left = `${
                  event.clientX - rect.x + offset
                }px`;
              }
              if (
                event.button === MouseButton.Left ||
                event.button === MouseButton.Middle
              ) {
                seeking.value = null;
                event.currentTarget.releasePointerCapture(event.pointerId);
                containerRef.current.style.cursor = '';
                playheadRef.current.style.display = '';
              }
            }}
          >
            <div
              className={styles.timeline}
              style={{width: `${sizes.fullLength}px`}}
            >
              <div
                className={styles.timelineContent}
                style={{
                  width: `${sizes.playableLength}px`,
                  left: `${sizes.paddingLeft}px`,
                }}
              >
                <Timestamps />
                <div className={styles.trackContainer}>
                  <SceneTrack />
                  {(tracks ?? []).map(track => (
                    <AudioTrackRow
                      key={track.id}
                      track={track}
                      clips={(allClips ?? []).filter(c => c.trackId === track.id)}
                    />
                  ))}
                </div>
                <Playhead seeking={seeking} />
              </div>
            </div>
            <div ref={playheadRef} className={styles.playheadPreview} />
          </div>
        </div>
      </div>
    </TimelineContextProvider>
  );
}
